import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ListChecks, Users, Timer, MessageCircleQuestion } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { DraftBadge } from "@/components/executive/ExecutiveHome";
import { Md, PauseReveal, LessonIntro } from "@/components/LessonBlocks";

const Scene = ({ s }) => (
  <section data-testid={`exec-scene-${s.slug}`} className="mt-10">
    <h2 className="font-heading text-2xl font-bold tracking-tight">{s.title}</h2>
    <div className="mt-4">{s.blocks.map((b, i) => (b.type === "pause"
      ? <PauseReveal key={i} b={b} id={`${s.slug}-${i}`} />
      : <div key={i} className="reader simple-reader"><Md>{b.body}</Md></div>))}</div>
  </section>
);

const Box = ({ testId, icon: Icon, title, tone, children }) => (
  <section data-testid={testId} className={`rounded-xl border-2 p-5 sm:p-6 ${tone}`}>
    <div className="flex items-center gap-2 font-heading text-lg font-bold"><Icon className="h-5 w-5" />{title}</div>
    <div className="mt-3">{children}</div>
  </section>
);

const LeaderBoxes = ({ s }) => (
  <div className="mt-12 space-y-5">
    <Box testId="exec-people-box" icon={Users} title="People to win" tone="border-brand-primary bg-brand-primary/10">
      <dl className="space-y-3">{s.people.map((p) => <div key={p.label}><dt className="text-sm font-semibold uppercase tracking-wide text-brand-primary">{p.label}</dt><dd className="mt-0.5 text-lg font-medium">{p.text}</dd></div>)}</dl>
    </Box>
    <Box testId="exec-ttv-box" icon={Timer} title="Time to value" tone="border-brand-action bg-brand-action/10">
      <div className="grid gap-3 sm:grid-cols-2">{s.time_to_value.map((x) => <div key={x.label} className="rounded-lg bg-card p-3"><div className="inline-block rounded-full bg-brand-action px-2.5 py-0.5 text-xs font-bold text-brand-action-fg">{x.label}</div><p className="mt-2 text-sm">{x.text}</p></div>)}</div>
    </Box>
    <Box testId="exec-decision-box" icon={ListChecks} title="Decision: go or no-go" tone="border-border bg-card">
      <div className="reader simple-reader"><Md>{s.decision}</Md></div>
    </Box>
    <Box testId="exec-ask-box" icon={MessageCircleQuestion} title="Ask your team" tone="border-border bg-muted/40">
      <ol className="list-decimal space-y-1.5 pl-5">{s.ask.map((q) => <li key={q}>{q}</li>)}</ol>
    </Box>
  </div>
);

export default function ExecutiveSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ["exec-session", sessionId], queryFn: async () => (await api.get(`/executive/sessions/${sessionId}`)).data });
  if (!s) return <Loader />;
  const next = s.number < s.count ? `LEADER-S${s.number + 1}` : null;
  const complete = async () => {
    try {
      await api.post(`/executive/sessions/${s.id}/complete`);
      qc.invalidateQueries({ queryKey: ["executive"] });
      qc.invalidateQueries({ queryKey: ["exec-session", sessionId] });
      toast.success("Session complete");
      navigate(next ? `/executive/sessions/${next}` : "/executive/check");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <PageShell>
      <div className="mx-auto max-w-3xl">
        <Link to="/learn" data-testid="exec-session-back" className="text-sm text-muted-foreground hover:text-foreground">← All sessions</Link>
        <div className="mt-6 flex flex-wrap items-center gap-2 overline-label">Session {s.number} of {s.count} · ~{s.minutes} min <DraftBadge status={s.status} testId="exec-session-draft" /></div>
        <h1 data-testid="exec-session-title" className="mt-2 font-heading text-4xl font-black tracking-tighter">{s.title}</h1>
        <LessonIntro lesson={s} />
        <div data-testid="exec-session-body">{s.sections.map((x) => <Scene key={x.slug} s={x} />)}</div>
        <LeaderBoxes s={s} />
        {s.next && <p data-testid="exec-next-teaser" className="mt-8 border-t border-border pt-4 italic text-muted-foreground"><span className="font-semibold not-italic text-foreground">Next · </span>{s.next}</p>}
        <button type="button" onClick={complete} data-testid="exec-session-complete" className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg">
          {s.completed ? "Continue" : "Mark session complete"} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </PageShell>
  );
}
